import { Injectable } from '@angular/core';
import chameleon from '@chamaeleonidae/chmln';
import * as fromRoot from '../../reducers';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { PermissionActions, Role, User } from '../models';
import { SkuService } from './sku.service';
import { UserService } from 'src/app/core/user.service';
import { MultiAdminService } from 'src/app/core/multiadmin.service';
import { BlendService } from './blend.service';
import { isUrlHistoryClean } from 'src/app/util/blend-helper';
import { ChargebeeService } from './chargebee.service';
import { bytesToGB } from '../func';

@Injectable({
  providedIn: 'root',
})
export class ChameleonService {
  private userSubscribe: Subscription;
  private chargebeeSubscription: Subscription;
  private user: User;
  private ONE_DAY = 1000 * 60 * 60 * 24; /* 1 day in ms */
  private isProcessing: boolean;

  constructor(
    private store: Store<fromRoot.State>,
    private skuService: SkuService,
    private chargebeeService: ChargebeeService,
    private userService: UserService,
    private multiadminService: MultiAdminService,
    private blendService: BlendService,
  ) {}

  public async identify(): Promise<void> {
    if (!isUrlHistoryClean()) {
      return;
    }
    this.userSubscribe = this.store.pipe(select(fromRoot.getAuthState)).subscribe(async (data) => {
      if (data.authenticated) {
        if (this.isProcessing) {
          return;
        }
        this.isProcessing = true;
        let trialEnds;
        let daysToRenewal;
        let renewalDate;
        let uid;
        this.user = data.user;
        let chargebeeCustomerID;
        let isTrial = this.user.is_on_trial ? 'yes' : 'no';
        let currencyCode;
        const currentStorageUtilizationGB = bytesToGB(this.user.disk_usage_bytes, 2);
        const currentStorageMaxGB = this.user.is_unlimited_plan
          ? 'Unlimited'
          : bytesToGB(this.user.disk_limit_bytes, 0);

        if (this.user) {
          if (this.userSubscribe) {
            this.userSubscribe.unsubscribe();
          }
          if (this.user.is_disableanalytics == 1) {
            // todo: if we want to enable tours for users who has disabled analytics
            // chameleon.identify(this.user.id);
            return;
          }

          chameleon.init('SxWyyKmLy9x1GLatcHr4iUUq3mbB4U6x9Ag6O1k00sZgDM-1TdLI1-FyoqEr7JgeAHXMQN', {
            fastUrl: 'https://fast.chameleon.io/',
          });

          let planName = this.user.plan_name;
          let cprofile;
          if (this.userService.checkPermission(PermissionActions.VIEW_BILLING)) {
            cprofile = await new Promise((resolve) => {
              this.chargebeeSubscription = this.chargebeeService.getSubscription().subscribe((data) => {
                if (data.isLoaded) {
                  if (this.chargebeeSubscription) {
                    this.chargebeeSubscription.unsubscribe();
                  }
                  resolve(data.cprofile);
                }
              });
            });
          } else {
            const cprofileData = await this.chargebeeService.getChargebeeProfile();
            cprofile = cprofileData?.cprofile;
          }

          if (cprofile?.subscription) {
            renewalDate = new Date(cprofile.subscription.subscriptionEndDate);
            daysToRenewal = this.getRemainingDays(cprofile.subscription.subscriptionEndDate);
            if (this.user.is_on_trial) {
              trialEnds = this.userService.dateConversionForTrials(
                cprofile.subscription.subscriptionEndDate,
                this.user.is_on_trial,
              );
            }
            planName = cprofile.subscription.planName;
            chargebeeCustomerID = cprofile.subscription.chargebeeCustomerID;
            isTrial = cprofile.subscription.isTrial;
            currencyCode = cprofile.subscription.currencyCode;
          }

          if (!uid) {
            uid = this.user.id;
          }

          const role = await this.getRole(this.user.id);
          const sku = await this.skuService.getSku();
          const planType = await this.skuService.getPlanType(sku);
          const created = new Date(this.user.join_date);
          const region = this.user.region;
          const chameleonObj = {
            created,
            company: {
              ...(renewalDate !== undefined ? { renewal_date: renewalDate } : {}),
              ...(uid !== undefined ? { uid } : {}),
              ...(trialEnds !== undefined ? { trial_ends: trialEnds } : {}),
              ...(daysToRenewal !== undefined ? { days_to_renewal: daysToRenewal } : {}),
              ...(role !== undefined ? { role } : {}),
              ...(chargebeeCustomerID !== undefined ? { CBID: chargebeeCustomerID } : {}),
              ...(isTrial !== undefined ? { IsTrial: isTrial } : {}),
              ...(currencyCode !== undefined ? { CurrencyCode: currencyCode } : {}),
              ...(currentStorageUtilizationGB !== undefined
                ? { CurrentStorageUtilizationGB: currentStorageUtilizationGB }
                : {}),
              ...(currentStorageMaxGB !== undefined ? { CurrentStorageMaxGB: currentStorageMaxGB } : {}),
              sku,
              plan_type: planType,
              plan_name: planName,
              region,
            },
          };
          chameleon.identify(this.user.id, chameleonObj);
        }
      }
    });
  }

  private getRemainingDays(dateStr: string): number {
    if (!dateStr || isNaN(new Date(dateStr).getTime())) {
      return;
    }
    const futureDate = new Date(dateStr);
    const today = new Date();
    if (futureDate <= today) {
      return 0;
    }
    const timeDifference = futureDate.getTime() - today.getTime();
    const daysDifference = timeDifference / this.ONE_DAY;
    return Math.round(daysDifference); // Round to the nearest day
  }

  private async getRole(userId: number): Promise<string> {
    if (this.userService.get('is_multi_new')) {
      const response = await this.multiadminService.getRoles();
      const roles = (response as Role[])['roles'] as Role[];
      for (const role of roles) {
        // Check if the role contains a user with the given user_id
        if (role.users.some((user) => user.user_id === userId)) {
          return role.name; // Return the role name if found
        }
      }
    }
    return;
  }
}
